<script setup lang="ts">
import {
  PhList as ListIcon,
  PhLockSimple as LockIcon,
  PhMoon as MoonIcon,
  PhSignOut as SignOutIcon,
  PhSun as SunIcon,
  PhUserCircle as UserCircleIcon,
  PhX as XIcon,
} from '@phosphor-icons/vue'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useSessionStore } from '../stores/session'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const menuOpen = ref(false)
const theme = ref<'light' | 'dark'>('light')

const themeLabel = computed(() => (theme.value === 'light' ? '切换深色主题' : '切换浅色主题'))

onMounted(() => {
  theme.value = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
})

watch(
  () => route.fullPath,
  () => {
    menuOpen.value = false
  },
)

function toggleTheme() {
  theme.value = theme.value === 'light' ? 'dark' : 'light'
  document.documentElement.dataset.theme = theme.value
  localStorage.setItem('cabinet-theme', theme.value)
}

async function signOut() {
  session.signOut()
  if (route.meta.requiresAuth) await router.replace('/login')
}
</script>

<template>
  <header class="site-header">
    <div class="container site-header__inner">
      <RouterLink class="brand" to="/" aria-label="栖居木作首页">
        <span class="brand__mark" aria-hidden="true">
          <span></span>
          <span></span>
        </span>
        <span class="brand__text">
          <strong>栖居木作</strong>
          <small>柜类有限定制</small>
        </span>
      </RouterLink>

      <nav class="desktop-nav" aria-label="主要导航">
        <RouterLink to="/products">选柜款</RouterLink>
        <RouterLink to="/trust">服务保障</RouterLink>
        <RouterLink v-if="session.isAuthenticated" to="/orders">订单进度</RouterLink>
        <RouterLink
          v-else
          class="nav-link--locked"
          :to="{ path: '/login', query: { redirect: '/orders' } }"
          aria-label="订单进度，需登录后查看"
          title="登录后查看订单进度"
        >
          <LockIcon :size="14" aria-hidden="true" />
          订单进度
        </RouterLink>
      </nav>

      <div class="header-actions">
        <button class="icon-button" type="button" :aria-label="themeLabel" @click="toggleTheme">
          <MoonIcon v-if="theme === 'light'" :size="20" aria-hidden="true" />
          <SunIcon v-else :size="20" aria-hidden="true" />
        </button>
        <RouterLink v-if="!session.isAuthenticated" class="button button--small button--secondary" to="/login">
          <UserCircleIcon :size="18" aria-hidden="true" />
          登录
        </RouterLink>
        <div v-else class="account-actions">
          <RouterLink class="account-link" to="/orders">{{ session.maskedPhone }}</RouterLink>
          <button class="icon-button" type="button" aria-label="退出登录" @click="signOut">
            <SignOutIcon :size="19" aria-hidden="true" />
          </button>
        </div>
        <button
          class="icon-button mobile-menu-button"
          type="button"
          :aria-expanded="menuOpen"
          aria-controls="mobile-navigation"
          :aria-label="menuOpen ? '关闭导航' : '打开导航'"
          @click="menuOpen = !menuOpen"
        >
          <XIcon v-if="menuOpen" :size="21" aria-hidden="true" />
          <ListIcon v-else :size="22" aria-hidden="true" />
        </button>
      </div>
    </div>

    <nav v-if="menuOpen" id="mobile-navigation" class="mobile-nav" aria-label="移动端导航">
      <div class="container mobile-nav__inner">
        <RouterLink to="/products">选择成熟柜款</RouterLink>
        <RouterLink to="/trust">查看服务保障</RouterLink>
        <RouterLink v-if="session.isAuthenticated" to="/orders">查看订单进度</RouterLink>
        <RouterLink
          v-else
          class="nav-link--locked"
          :to="{ path: '/login', query: { redirect: '/orders' } }"
          aria-label="查看订单进度，需登录"
        >
          <LockIcon :size="15" aria-hidden="true" />
          查看订单进度
        </RouterLink>
        <RouterLink v-if="!session.isAuthenticated" to="/login">手机号登录</RouterLink>
        <div v-else class="mobile-account">
          <RouterLink to="/orders">{{ session.maskedPhone }}</RouterLink>
          <button type="button" @click="signOut">
            <SignOutIcon :size="18" aria-hidden="true" />
            退出登录
          </button>
        </div>
      </div>
    </nav>
  </header>
</template>

<style scoped>
.nav-link--locked {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  opacity: 0.68;
}

.desktop-nav .nav-link--locked:hover,
.desktop-nav .nav-link--locked.router-link-active {
  color: var(--text-soft);
  opacity: 0.88;
}

.desktop-nav .nav-link--locked.router-link-active::after {
  transform: scaleX(0);
}

@media (max-width: 760px) {
  .mobile-nav .nav-link--locked {
    display: flex;
    color: var(--muted);
  }
}
</style>
